import React, { useEffect, useState } from "react";
import {
  X,
  Loader2,
  RefreshCw,
  ArrowLeft,
  ListOrdered,
  CheckCircle2,
} from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  useGetProductWishesQuery,
  useGetPlayTableHistoryQuery,
  useCompleteProductWishMutation,
} from "state/api";
import {
  formatLeaderboardTime,
  shouldShowLeaderboardQuestions,
} from "utils/prizeDisplay";

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN");
  } catch {
    return String(d);
  }
};

const statusClass = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "ONGOING")
    return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
  if (s === "PENDINGRESULT" || s === "PENDINGRESULT_EVENT")
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
  if (s === "PROCESSED" || s === "COMPLETED" || s === "DONE")
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
  return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
};

const payoutStatusClass = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "PAID")
    return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
  if (s === "PENDING" || s === "AWAITING_RESULT")
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
  if (s === "FAILED")
    return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
  if (s === "PRODUCT")
    return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
};

const payoutStatusLabel = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "PAID") return "Paid";
  if (s === "PENDING") return "Pending payout";
  if (s === "AWAITING_RESULT") return "Awaiting result";
  if (s === "FAILED") return "Failed";
  if (s === "PRODUCT") return "Product prize";
  if (s === "NOT_APPLICABLE") return "N/A";
  return status || "—";
};

const ProductWishSidebar = ({ open, onClose, product }) => {
  const [selectedWish, setSelectedWish] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [completeTarget, setCompleteTarget] = useState(null);
  const pageSize = 25;

  const productId = product?._id;

  const [completeProductWish, { isLoading: completingWish }] =
    useCompleteProductWishMutation();

  const {
    data: wishesData,
    isLoading: wishesLoading,
    isFetching: wishesFetching,
    error: wishesError,
    refetch: refetchWishes,
  } = useGetProductWishesQuery(productId, {
    skip: !open || !productId,
  });

  const {
    data: historyData,
    isLoading: historyLoading,
    isFetching: historyFetching,
    error: historyError,
    refetch: refetchHistory,
  } = useGetPlayTableHistoryQuery(
    {
      productId,
      playToken: selectedWish?.playToken,
      page: historyPage,
      pageSize,
    },
    {
      skip: !open || !productId || !selectedWish?.playToken,
    }
  );

  useEffect(() => {
    if (!open) {
      setSelectedWish(null);
      setHistoryPage(1);
      setCompleteTarget(null);
    }
  }, [open]);

  useEffect(() => {
    setHistoryPage(1);
  }, [selectedWish?.playToken]);

  if (!open || !product) return null;

  const wishes = wishesData?.productWishDocuments ?? [];
  const history = historyData?.history ?? [];
  const totalCount = historyData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const payoutSummary = historyData?.payoutSummary;
  const payoutEligible = payoutSummary?.totalEligible ?? 0;
  const payoutPaid = payoutSummary?.paid ?? 0;
  const payoutPending = payoutSummary?.pending ?? 0;
  const payoutFailed = payoutSummary?.failed ?? 0;
  const payoutProgressPct =
    payoutEligible > 0 ? Math.round((payoutPaid / payoutEligible) * 100) : 0;

  const userLabel = (entry) => {
    const u = entry.userDetails;
    if (!u) return entry.userId || "—";
    const name = [u.name, u.lastName].filter(Boolean).join(" ").trim();
    return name || u.phone || entry.userId;
  };

  const isOngoing = (wish) =>
    (wish?.status || "").toUpperCase() === "ONGOING";

  const handleCompleteClick = (wish, e) => {
    e?.stopPropagation?.();
    setCompleteTarget(wish);
  };

  const handleConfirmComplete = async () => {
    if (!completeTarget?._id || !productId) return;
    try {
      const result = await completeProductWish({
        productWishId: completeTarget._id,
        productId,
      }).unwrap();
      toast.success(
        result.playTokenRotated
          ? "Round marked PENDINGRESULT. Product playToken rotated and a new ONGOING round was created."
          : "Round marked PENDINGRESULT.",
        { position: "top-right" }
      );
      if (selectedWish?._id === completeTarget._id) {
        setSelectedWish(null);
      }
      setCompleteTarget(null);
      refetchWishes();
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Failed to complete round",
        { position: "top-right" }
      );
    }
  };

  const renderCompleteButton = (wish, { compact = false } = {}) => {
    if (!isOngoing(wish)) return null;
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={
          compact
            ? "h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
            : "w-full text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
        }
        onClick={(e) => handleCompleteClick(wish, e)}
      >
        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
        Mark Pending Result
      </Button>
    );
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 z-50 h-full w-[70%] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col"
        role="dialog"
        aria-label="Product competition rounds"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-gray-900 dark:text-white">
              {selectedWish ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0 -ml-1"
                  onClick={() => setSelectedWish(null)}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              ) : (
                <ListOrdered className="h-5 w-5 shrink-0" />
              )}
              <h2 className="text-lg font-semibold truncate">
                {selectedWish ? "Leaderboard" : "Competition rounds"}
              </h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
              {product.productName}
            </p>
            <p className="text-xs text-gray-500 font-mono truncate">{productId}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="shrink-0 h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {!selectedWish ? (
          <>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0 flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {wishes.length} round{wishes.length !== 1 ? "s" : ""}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchWishes()}
                disabled={wishesFetching}
                className="h-8"
              >
                {wishesFetching ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                <span className="ml-1.5">Refresh</span>
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {wishesLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : wishesError ? (
                <p className="text-sm text-red-600 dark:text-red-400 text-center py-8">
                  Failed to load competition rounds.
                </p>
              ) : wishes.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">
                  No product wish documents found.
                </p>
              ) : (
                <div className="space-y-2">
                  {wishes.map((wish) => (
                    <div
                      key={wish._id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedWish(wish)}
                        className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-mono text-gray-500 truncate">
                              {wish.playToken}
                            </p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                              {wish.fillWish ?? 0} entries
                              {wishesData?.product?.totalWish
                                ? ` / ${wishesData.product.totalWish}`
                                : ""}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {wish.entryCount ?? 0} player
                              {(wish.entryCount ?? 0) !== 1 ? "s" : ""} on
                              leaderboard
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClass(wish.status)}`}
                            >
                              {wish.status || "—"}
                            </span>
                            {wish.isCurrentRound && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 font-medium">
                                Current
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Updated {formatDate(wish.updatedAt || wish.timestamp)}
                        </p>
                      </button>
                      {isOngoing(wish) && (
                        <div className="px-3 pb-3">
                          {renderCompleteButton(wish)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-xs font-mono text-gray-500 truncate">
                    {selectedWish.playToken}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
                    <span
                      className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mr-2 ${statusClass(selectedWish.status)}`}
                    >
                      {selectedWish.status}
                    </span>
                    {totalCount} entr{totalCount !== 1 ? "ies" : "y"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {renderCompleteButton(selectedWish, { compact: true })}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchHistory()}
                    disabled={historyFetching}
                    className="h-8"
                  >
                    {historyFetching ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
              {payoutEligible > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>Payout progress</span>
                    <span>
                      {payoutPaid}/{payoutEligible} paid
                      {payoutPending > 0 ? ` · ${payoutPending} pending` : ""}
                      {payoutFailed > 0 ? ` · ${payoutFailed} failed` : ""}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-green-500 transition-all duration-300"
                      style={{ width: `${payoutProgressPct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {historyLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : historyError ? (
                <p className="text-sm text-red-600 dark:text-red-400 text-center py-8 px-5">
                  Failed to load leaderboard.
                </p>
              ) : historyData?.message ===
                "No matching play table history found" ? (
                <p className="text-sm text-gray-500 text-center py-8 px-5">
                  No leaderboard entries for this round yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Player</TableHead>
                      <TableHead className="text-center">Correct</TableHead>
                      <TableHead className="text-center">Time</TableHead>
                      <TableHead className="text-center">Payout</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell className="font-medium">
                          {row.rank === 0 ? "#" : row.rank}
                        </TableCell>
                        <TableCell>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {userLabel(row)}
                            </p>
                            {row.userDetails?.phone && (
                              <p className="text-xs text-gray-500 font-mono truncate">
                                {row.userDetails.phone}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {shouldShowLeaderboardQuestions(row) ? (
                            <>
                              {row.correctQuestion ?? "—"}/{row.totalQuestion ?? "—"}
                            </>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="text-center text-xs">
                          {formatLeaderboardTime(row)}
                        </TableCell>
                        <TableCell className="text-center">
                          {row.payoutStatus &&
                          row.payoutStatus !== "NOT_APPLICABLE" ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span
                                className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${payoutStatusClass(row.payoutStatus)}`}
                              >
                                {payoutStatusLabel(row.payoutStatus)}
                              </span>
                              {row.payoutAmount && (
                                <span className="text-xs text-gray-500">
                                  ₹{row.payoutAmount}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {totalCount > pageSize && (
              <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={historyPage <= 1 || historyFetching}
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-xs text-gray-500">
                  Page {historyPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={historyPage >= totalPages || historyFetching}
                  onClick={() =>
                    setHistoryPage((p) => Math.min(totalPages, p + 1))
                  }
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </aside>

      <Dialog
        open={!!completeTarget}
        onOpenChange={(isOpen) => {
          if (!isOpen) setCompleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark competition PENDINGRESULT?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  This will close the ONGOING round{" "}
                  <span className="font-mono text-xs">
                    {completeTarget?.playToken}
                  </span>{" "}
                  and set its status to <strong>PENDINGRESULT</strong>.
                </p>
                {completeTarget?.isCurrentRound ? (
                  <p>
                    This is the <strong>current</strong> round — same as when
                    entries hit 100% in the app: product{" "}
                    <strong>playToken will rotate</strong>,{" "}
                    <strong>isPlayable</strong> set to NO, and a new ONGOING
                    round starts with fillWish 0.
                  </p>
                ) : (
                  <p>
                    This is an older round — only its status will change to
                    PENDINGRESULT (no playToken rotation).
                  </p>
                )}
                <p className="text-amber-700 dark:text-amber-400">
                  The payout cron will process{" "}
                  <strong>PENDINGRESULT</strong> rounds and mark them
                  PROCESSED.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmComplete} disabled={completingWish}>
              {completingWish ? "Updating..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductWishSidebar;
